import { type RefObject, useImperativeHandle, useRef } from "react";
import PreviewTableRow from "./PreviewTableRow";
import { type PreviewItem, type PreviewItemUpdateData } from "../../models";
import classNames from "classnames";
import { HAS_END_CUSTOMER } from "../../../utils/constants";
import { canUseCommentsForProduct } from "../../../utils/productUtils";
import { distinct, isNonNull } from "../../../utils/reducers";
import { userCanSeePriceReference } from "../../../Cart/components/CartPage/LineItemStaticParams";
import { useTranslation } from "react-i18next";
import { useVirtualizer } from "@tanstack/react-virtual";
import CommentsDisabledOutlinedIcon from "@mui/icons-material/CommentsDisabledOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

export interface PreviewTableRef {
  scrollToRow: (index: number) => void;
}

interface Props {
  items: PreviewItem[];
  onDataChange: (index: number, newData: Partial<PreviewItemUpdateData>) => void;
  onRemoveItem: (index: number) => void;
  onRemoveAllItems: () => void;
  onChangeAllCommentsVisibility: (visible: boolean) => void;
  onChangeCommentVisibility: (index: number, visible: boolean) => void;
  saleOnly: boolean;
  setSaleOnly: (visible: boolean) => void;
  listRef: RefObject<PreviewTableRef>;
}

const PreviewTable = ({
  items,
  onDataChange,
  onRemoveItem,
  onRemoveAllItems,
  onChangeAllCommentsVisibility,
  onChangeCommentVisibility,
  listRef,
}: Props) => {
  const { t } = useTranslation();

  const virtualListContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => 220,
    overscan: 8,
    getScrollElement: () => internalTableRef.current?.closest(".scha__modal__contents__children") ?? null,
    scrollPaddingEnd: 100,
    scrollPaddingStart: 100,
    // @ts-expect-error
    shouldAdjustScrollPositionOnItemSizeChange: () => true,
  });

  const internalTableRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(
    listRef,
    () => ({
      scrollToRow: (index) => {
        rowVirtualizer.scrollToIndex(index, { align: "start" });

        rowVirtualizer.scrollElement?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({
          block: "center",
        });
      },
    }),
    [],
  );

  const showPriceRefColumn = userCanSeePriceReference();

  const allCommentsOpen = items.every((item) => item.isCommentVisible);

  const canMakeComments = distinct(items.map((item) => item.marketType).filter(isNonNull)).some((marketType) =>
    canUseCommentsForProduct(marketType),
  );

  const showPriceReferenceColumn = showPriceRefColumn && !HAS_END_CUSTOMER;

  return (
    <div
      className={classNames("scha__bulk-preview__table", showPriceRefColumn && "scha__bulk-preview__table--priceRef")}
      ref={internalTableRef}
    >
      <div className={"scha__bulk-preview__table__header text-color"} style={{ marginBottom: "16px" }}>
        <div>{t("wishlistPage.table.header.number")}</div>
        <div>{t("bulkUpload.table.header.productId")}</div>
        <div>{t("cartPage.table.header.quantity.title")}</div>
        <div>{t("cartPage.table.header.delivery.title")}</div>

        {showPriceReferenceColumn && <div>{t("cartPage.table.header.priceReference.title")}</div>}

        <div className={"d-flex flex-justify-end"}>
          {canMakeComments &&
            (allCommentsOpen ? (
              <CommentsDisabledOutlinedIcon
                className="icon-clickable icon-clickable--primary"
                onClick={() => onChangeAllCommentsVisibility(!allCommentsOpen)}
                fontSize="medium"
              />
            ) : (
              <CommentOutlinedIcon
                className="icon-clickable icon-clickable--primary"
                onClick={() => onChangeAllCommentsVisibility(!allCommentsOpen)}
                fontSize="medium"
              />
            ))}
          <DeleteOutlineOutlinedIcon
            className="icon-clickable icon-clickable--primary ml-3"
            onClick={onRemoveAllItems}
            fontSize="medium"
          />
        </div>
      </div>

      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
        ref={virtualListContainerRef}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => (
          <div
            key={virtualRow.index}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <PreviewTableRow
              key={virtualRow.index}
              index={virtualRow.index}
              data={items[virtualRow.index]}
              onDataChange={onDataChange}
              onSetCommentVisibility={onChangeCommentVisibility}
              onRemoveItem={onRemoveItem}
              showPriceReference={showPriceReferenceColumn}
              onRender={rowVirtualizer.measureElement}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PreviewTable;
